import { Container } from 'pixi.js';
import type { LayerStackAsset } from '../../layerstack/LayerStackLoader';
import type { LayerStackSprite } from '../../layerstack/LayerStackSprite';
import { FxTimebase } from './FxTimebase';
import { OneShotFx } from './OneShotFx';
import { PixelBurst } from './PixelBurst';
import { Projectiles, type ProjectileHitTest, type ProjectileImpact } from './Projectiles';
import {
  ENEMY_HIT_FLASH_MS,
  PLAYER_DAMAGED_FLASH_MS,
  PLAYER_FIRING_FLASH_MS,
  StateFlash,
} from './StateFlash';

/**
 * The one facade the game and the harness call. It owns the pools and the
 * timebase; the renderer only lends it a layer and takes trauma, which keeps
 * R2Renderer free of any fx/ import (FxHost is structural).
 */
export interface FxHost {
  addFx(child: Container): void;
  addTrauma(amount: number): void;
}

/**
 * The audio channel of the FX events. Every visual beat below announces itself
 * here, which is how a cue that changes no sprite state (an armour impact, a
 * pierce landing) can still be heard: the state-change binding never saw those.
 * Structural on purpose — FxDirector must not know about the audio engine.
 */
export interface FxAudioSink {
  shot(): void;
  pierceShot(): void;
  impactSmall(): void;
  impactArmor(): void;
  enemyHit(): void;
  enemyKill(): void;
  playerDamaged(): void;
}

export const FX_SPARK_SMALL = 'small-hit-spark';
export const FX_SPARK_ARMOR = 'armor-hit';
export const FX_SPARK_CORE = 'core-hit';
export const FX_BULLET_ORDINARY = 'ordinary-bullet';
export const FX_BULLET_PIERCING = 'piercing-bullet';

export const FX_ONE_SHOT_SLUGS: readonly string[] = Object.freeze([FX_SPARK_SMALL, FX_SPARK_ARMOR, FX_SPARK_CORE]);
export const FX_PROJECTILE_SLUGS: readonly string[] = Object.freeze([FX_BULLET_ORDINARY, FX_BULLET_PIERCING]);
export const FX_SLUGS: readonly string[] = Object.freeze([...FX_ONE_SHOT_SLUGS, ...FX_PROJECTILE_SLUGS]);

export const PLAYER_FIRING_STATE = 'firing';
export const PLAYER_DAMAGED_STATE = 'damaged';
export const ENEMY_HIT_STATE = 'hit';
export const ENEMY_DYING_STATE = 'dying';
const PLAYER_NOSE_ANCHOR = 'noseEmitter';

export const PROJECTILE_SPEED_PX_PER_SECOND = 420;
const IMPACT_SMALL_TRAUMA = 0.18;
const IMPACT_ARMOR_TRAUMA = 0.22;
const IMPACT_ARMOR_FREEZE_MS = 40;
const ENEMY_KILL_TRAUMA = 0.45;
const ENEMY_KILL_FREEZE_MS = 100;
/** Exported so an integrated path that owns the flash itself still shakes and freezes identically. */
export const PLAYER_DAMAGED_TRAUMA = 0.35;
export const PLAYER_DAMAGED_FREEZE_MS = 60;

const NO_HIT: ProjectileHitTest = () => false;
const NO_IMPACT = (): void => undefined;
const NO_AUDIO: FxAudioSink = {
  shot: () => undefined,
  pierceShot: () => undefined,
  impactSmall: () => undefined,
  impactArmor: () => undefined,
  enemyHit: () => undefined,
  enemyKill: () => undefined,
  playerDamaged: () => undefined,
};

export class FxDirector {
  readonly timebase = new FxTimebase();
  private readonly projectiles: Projectiles;
  private readonly bursts: PixelBurst;
  private readonly oneShots: OneShotFx;
  private readonly flashes = new StateFlash();
  private hitTest: ProjectileHitTest = NO_HIT;
  private onImpact: (impact: ProjectileImpact) => void = NO_IMPACT;
  private audio: FxAudioSink = NO_AUDIO;

  constructor(private readonly host: FxHost) {
    const shotLayer = new Container();
    const burstLayer = new Container();
    const sparkLayer = new Container();
    // Painted bottom to top: shots, then debris, then the impact sprite.
    host.addFx(shotLayer);
    host.addFx(burstLayer);
    host.addFx(sparkLayer);
    this.projectiles = new Projectiles(shotLayer);
    this.bursts = new PixelBurst(burstLayer);
    this.oneShots = new OneShotFx(sparkLayer);
  }

  /** Boot-time: every FX slug gets its pool before the first frame that needs it. */
  register(slug: string, asset: LayerStackAsset): void {
    if (FX_PROJECTILE_SLUGS.includes(slug)) {
      this.projectiles.register(slug, asset);
      return;
    }
    this.oneShots.register(slug, asset);
  }

  setProjectileTarget(hitTest: ProjectileHitTest, onImpact: (impact: ProjectileImpact) => void): void {
    this.hitTest = hitTest;
    this.onImpact = onImpact;
  }

  /** Optional: without a sink the FX are silent, which is what the old path did. */
  setAudioSink(sink: FxAudioSink | null): void {
    this.audio = sink ?? NO_AUDIO;
  }

  get liveParticles(): number {
    return this.bursts.liveCount;
  }

  get liveProjectiles(): number {
    return this.projectiles.liveCount;
  }

  get liveSparks(): number {
    return this.oneShots.liveCount;
  }

  playerFire(player: LayerStackSprite, slug: string): void {
    this.flashes.flash(player, PLAYER_FIRING_STATE, PLAYER_FIRING_FLASH_MS);
    this.fireProjectileOnly(player, slug);
  }

  /**
   * The shot without the flash: when `firing` is a held state owned by the
   * controller, a 200ms flash on top of it would fight its writer.
   */
  fireProjectileOnly(player: LayerStackSprite, slug: string): void {
    const hasNose = PLAYER_NOSE_ANCHOR in player.manifest.anchors;
    const nose = hasNose ? player.getAnchor(PLAYER_NOSE_ANCHOR) : null;
    // Renderer-local: actors sit in flat renderer space, so local anchor + sprite x.
    const x = nose ? player.x + nose.localX : player.x + player.targetWidth;
    const y = nose ? player.y + nose.localY : player.y + player.targetHeight / 2;
    this.projectiles.fire(slug, x, y, PROJECTILE_SPEED_PX_PER_SECOND);
    if (slug === FX_BULLET_PIERCING) this.audio.pierceShot();
    else this.audio.shot();
  }

  impactSmall(x: number, y: number): void {
    this.oneShots.spawn(FX_SPARK_SMALL, x, y);
    this.bursts.burst('small', x, y);
    this.host.addTrauma(IMPACT_SMALL_TRAUMA);
    this.audio.impactSmall();
  }

  impactArmor(x: number, y: number): void {
    this.oneShots.spawn(FX_SPARK_ARMOR, x, y);
    this.host.addTrauma(IMPACT_ARMOR_TRAUMA);
    this.timebase.freeze(IMPACT_ARMOR_FREEZE_MS);
    this.audio.impactArmor();
  }

  enemyHit(sprite: LayerStackSprite, x: number, y: number): void {
    this.impactSmall(x, y);
    this.flashes.flash(sprite, ENEMY_HIT_STATE, ENEMY_HIT_FLASH_MS);
    this.audio.enemyHit();
  }

  enemyKill(sprite: LayerStackSprite, x: number, y: number): void {
    this.oneShots.spawn(FX_SPARK_CORE, x, y);
    this.bursts.burst('kill', x, y);
    this.host.addTrauma(ENEMY_KILL_TRAUMA);
    this.timebase.freeze(ENEMY_KILL_FREEZE_MS);
    this.flashes.hold(sprite, ENEMY_DYING_STATE);
    this.audio.enemyKill();
  }

  playerDamaged(sprite: LayerStackSprite): void {
    this.flashes.flash(sprite, PLAYER_DAMAGED_STATE, PLAYER_DAMAGED_FLASH_MS);
    this.host.addTrauma(PLAYER_DAMAGED_TRAUMA);
    this.timebase.freeze(PLAYER_DAMAGED_FREEZE_MS);
    this.audio.playerDamaged();
  }

  /** Driven with the timebase-scaled delta, so a hit stop pauses every subsystem. */
  update(deltaMs: number): void {
    this.oneShots.update(deltaMs);
    this.bursts.update(deltaMs);
    this.flashes.update(deltaMs);
    for (const impact of this.projectiles.update(deltaMs, this.hitTest)) this.onImpact(impact);
  }
}
